import React, { useMemo } from 'react';
import { Card, Table, Tag, Typography, Spin, Alert } from 'antd';
import { useCustomerRankingByRevenue } from '../hooks';

const { Title, Text } = Typography;

interface CustomerPerformanceTabProps {
  dateRange?: string;
  region?: number;
}

export const CustomerPerformanceTab: React.FC<CustomerPerformanceTabProps> = ({
  dateRange = 'all',
  region,
}) => {
  // Fetch customer ranking by revenue data from API
  const { data, isLoading, error } = useCustomerRankingByRevenue(dateRange, region);

  // Transform API data to match the table structure
  const topCustomers = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map(customer => ({
      rank: customer.rank,
      name: customer.accountName,
      quotes: customer.totalQuotes,
      revenue: customer.totalRevenue,
      avgValue: customer.averageQuoteValue,
    }));
  }, [data]);
  const columns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      sorter: (a: { rank: number }, b: { rank: number }) => a.rank - b.rank,
      render: (rank: number) => (
        <Tag color={rank <= 3 ? 'blue' : 'default'} className="font-bold">
          #{rank}
        </Tag>
      ),
    },
    {
      title: 'Customer Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      render: (name: string) => <span className="font-semibold">{name}</span>,
    },
    {
      title: 'Total Quotes',
      dataIndex: 'quotes',
      key: 'quotes',
      align: 'right' as const,
      sorter: (a: { quotes: number }, b: { quotes: number }) => a.quotes - b.quotes,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      sorter: (a: { revenue: number }, b: { revenue: number }) => a.revenue - b.revenue,
      render: (revenue: number) => `£${revenue.toLocaleString()}`,
    },
    {
      title: 'Avg Quote Value',
      dataIndex: 'avgValue',
      key: 'avgValue',
      align: 'right' as const,
      sorter: (a: { avgValue: number }, b: { avgValue: number }) => a.avgValue - b.avgValue,
      render: (avgValue: number) => `£${avgValue}`,
    },
  ];

  return (
    <Card>
      <Title level={4}>Top Quoting Customers - Leaderboard</Title>
      <Text type="secondary" className="block mb-4">
        Customers ranked by quote volume and revenue
      </Text>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          message="Error Loading Data"
          description="Failed to load customer ranking data. Please try again."
          type="error"
          showIcon
        />
      ) : (
        <Table
          columns={columns}
          dataSource={topCustomers}
          rowKey="rank"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} customers`,
          }}
        />
      )}
    </Card>
  );
};
